import type { Hero } from 'types';

import React from 'react';

import Image from 'next/image';

import {
  useFeatureFlagEnabled,
  useFeatureFlagVariantKey,
} from 'posthog-js/react';

import { Paragraph, Wrap, ButtonClose } from '@leafwell/components';

import { useCurrentState } from 'hooks/useCurrentState';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useEventsTracker } from 'contexts/eventsTracker';
import Figure from 'components/figure';
import StateSelect from 'components/state-select';
import HeadingBody from 'components/heading-body';

import { priceExperiments } from 'lib/posthog';
import { useHasMounted } from 'hooks/useHasMounted';

const SlideUpMessage: React.FC<{
  visible: boolean;
  toggleMessage: (toggle: boolean) => void;
}> = ({ visible, toggleMessage }) => {
  const stateCode = useCurrentState();
  const { cardPage, statePages } = useWordPressContext();
  const { trackEvent } = useEventsTracker();
  const hasStateCode = stateCode && stateCode !== '';
  let url: string;
  let price: string;

  const hasMounted = useHasMounted();

  const priceExperimentFlags = priceExperiments.map(experiment => {
    return {
      ...experiment,
      isEnabled: useFeatureFlagEnabled(experiment.featureFlag),
      variant: useFeatureFlagVariantKey(experiment.featureFlag),
    };
  });

  const priceExperiment = priceExperimentFlags.find(
    exp => exp.isEnabled && exp.variant !== 'control',
  );

  const hasActiveExperiment = priceExperiment !== undefined && hasMounted;

  if (hasStateCode) {
    const statePage = statePages.find(({ code }) => code === stateCode);
    url = statePage.uri;

    if (statePage.price) {
      price = statePage.price;
    }
  } else {
    url = cardPage?.uri;
  }

  function handleClick(key?: string | null) {
    trackEvent({
      event: 'select_content',
      content_type: 'limited_time_offer',
      item_id: key ? key : 'page',
      origin: 'homepage',
    });
  }

  return (
    <div
      className={[
        'relative md:fixed md:bottom-8 md:rounded-lg bg-[#FFB800] z-10 transition-all duration-500',
        visible
          ? 'max-h-80 md:max-h-full right-0 md:right-8'
          : 'max-h-0 md:max-h-full md:-right-8 opacity-0',
      ].join(' ')}
    >
      <div className="flex justify-between items-start py-6 px-6 md:gap-x-8">
        <div className="flex flex-col">
          <p className="text-xl font-medium" suppressHydrationWarning>
            {hasMounted ? (
              hasActiveExperiment ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: priceExperiment?.homePageCta?.title,
                  }}
                />
              ) : price ? (
                `Get approved for just $${price}`
              ) : (
                'Get approved today'
              )
            ) : (
              'Get approved today'
            )}
          </p>
          <p className="text-xl">
            {hasActiveExperiment
              ? priceExperiment?.homePageCta?.description
              : 'Limited offer'}
          </p>
          <a
            href={hasActiveExperiment ? priceExperiment?.homePageCta?.url : url}
            className="mt-4 relative select-none cursor-pointer whitespace-nowrap inline-grid gap-x-2 items-center justify-center text-center text-sm font-medium outline-none hover:underline leading-none border rounded-md bg-white min-h-50 p-4 w-fit"
            onClick={() =>
              handleClick(
                hasActiveExperiment ? priceExperiment?.homePageCta?.key : null,
              )
            }
          >
            {hasActiveExperiment
              ? priceExperiment?.homePageCta?.label
              : 'Get your card now'}
          </a>
        </div>
        <Image src="promotion.svg" alt="" width={126} height={118} />
        <ButtonClose
          onClick={() => toggleMessage(false)}
          className="w-6 mt-2 absolute top-4 right-2"
          size={12}
        />
      </div>
    </div>
  );
};

const HomeHeroVariant: React.FC<Hero> = ({ image, title, steps }) => {
  const [showMessage, setShowMessage] = React.useState(true);
  return (
    <>
      {/*       <SlideUpMessage visible={showMessage} toggleMessage={setShowMessage} />
       */}{' '}
      {/* <TaxSavingsCalculator /> */}
      <section className="relative bg-gradient-to-r from-gray-200 via-gray-200 to-lilac-100 xl:-mt-10 xl:pt-10 before:absolute before:md:w-3/4 before:h-full before:md:right-0 before:md:bottom-0 before:bg-gradient-to-b before:from-lilac-100 before:to-green-200 before:gradient-mask-l-0">
        <Wrap
          spacer={false}
          className="lg:grid lg:grid-cols-3 lg:gap-x-3 pt-6 xl:pt-0 pb-12 lg:pb-0 relative lg:min-h-[540px] xl:min-h-[580px] transition-all"
        >
          <div className="lg:self-center grid gap-y-6 xl:gap-y-12 lg:pb-12 col-span-2">
            <div>
              <HeadingBody
                level="1"
                tagName="h1"
                title={title}
                className="font-semibold"
              />
            </div>
            <StateSelect showLoginCta={true} />
            {steps && (
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-6 xl:pt-3">
                {steps.map(({ title, description }, key) => (
                  <li
                    key={key}
                    className="grid grid-cols-2-auto gap-x-3 gap-y-1 items-start"
                  >
                    <Image
                      height={20}
                      width={20}
                      priority={true}
                      src="/check-gradient.svg"
                      alt=""
                      className="row-span-2 mt-list-offset"
                    />
                    <Paragraph
                      size="large"
                      className="font-semibold"
                      dangerouslySetInnerHTML={{ __html: title }}
                    />
                    <Paragraph
                      size="small"
                      variant="medium"
                      dangerouslySetInnerHTML={{ __html: description }}
                      tagName="article"
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
          {image && (
            <Figure
              alt={image.alt}
              src={image.src}
              width={image.width}
              height={image.height}
              priority={true}
              className="hidden lg:block lg:self-end"
              sizes="(max-width: 1023px) 0vw, (max-width: 1200px) 33vw, 33vw"
            />
          )}
        </Wrap>
      </section>
    </>
  );
};

export default HomeHeroVariant;
